import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  FunnelIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  TableCellsIcon,
  CalendarIcon,
  ClipboardDocumentListIcon,
  UserGroupIcon,
  HomeIcon,
} from '@heroicons/react/24/outline';
import SortOrderButton from '../components/ui/SortOrderButton';
import { GENDER_ICON_FEMALE, GENDER_ICON_MALE } from '../utils/genderDisplay';
import { Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-hot-toast';
import { breedingApi } from '../api/breeding';
import type { BreedingPair, CreateBreedingPairDto } from '../api/breeding';
import { format } from 'date-fns';
import AddBreedingPairModal from '../components/AddBreedingPairModal';
import EditBreedingPairModal from '../components/EditBreedingPairModal';
import ViewBreedingPairModal from '../components/ViewBreedingPairModal';
import ManageClutchesModal from '../components/ManageClutchesModal';
import BreedingPairAvatars from '../components/breeding/BreedingPairAvatars';
import { getBirdDisplayId } from '../utils/birdDisplay';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';
import CageSelect from '../components/CageSelect';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../components/ui/BrandModal';
import { EggStatus } from '../utils/enums';
import { daysAgo, type BreedingListFilter } from '../utils/listFilterPaths';

interface BreedingFilters {
  status?: 'active' | 'ended';
  cageNumber?: string;
  speciesId?: string;
  focus?: BreedingListFilter;
  dateRange?: {
    start: string;
    end: string;
  };
}

export default function Breeding() {
  // Data state
  const [breedingPairs, setBreedingPairs] = useState<BreedingPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<BreedingFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof BreedingPair>('pairingBeginDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showManageClutchesModal, setShowManageClutchesModal] = useState(false);
  const [selectedBreedingPair, setSelectedBreedingPair] = useState<BreedingPair | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const focus = searchParams.get('filter') as BreedingListFilter | null;
    const status = searchParams.get('status') as 'active' | 'ended' | null;
    const next: BreedingFilters = {};
    if (focus === 'productive' || focus === 'attention') {
      next.focus = focus;
    }
    if (status === 'active' || status === 'ended') {
      next.status = status;
    }
    if (Object.keys(next).length > 0) {
      setFilters(next);
      setShowFilters(true);
    }
  }, [searchParams]);

  const loadData = async () => {
    try {
      setLoading(true);
      const pairsResponse = await breedingApi.getAll();
      setBreedingPairs(pairsResponse.data);
      setError(null);
    } catch (err) {
      setError('Failed to load breeding data');
      toast.error('Failed to load breeding data');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  const filteredBreedingPairs = useMemo(() => {
    let result = breedingPairs;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (pair) =>
          pair.maleBirdEntity?.bandId.toLowerCase().includes(term) ||
          pair.femaleBirdEntity?.bandId.toLowerCase().includes(term) ||
          pair.maleBirdEntity?.species?.commonName?.toLowerCase().includes(term) ||
          pair.femaleBirdEntity?.species?.commonName?.toLowerCase().includes(term) ||
          pair.cageNumber.toLowerCase().includes(term) ||
          pair.nestNumber?.toLowerCase().includes(term) ||
          pair.comments?.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status) {
      result = result.filter((pair) => 
        filters.status === 'active' ? !pair.pairingEndDate : !!pair.pairingEndDate
      );
    }
    if (filters.cageNumber) {
      result = result.filter((pair) => pair.cageNumber === filters.cageNumber);
    }
    if (filters.speciesId) {
      result = result.filter((pair) => 
        pair.maleBirdEntity?.species?.id === filters.speciesId ||
        pair.femaleBirdEntity?.species?.id === filters.speciesId
      );
    }
    if (filters.dateRange) {
      result = result.filter((pair) => {
        if (!pair.pairingBeginDate) return false;
        const pairDate = new Date(pair.pairingBeginDate);
        if (isNaN(pairDate.getTime())) return false;
        const startDate = new Date(filters.dateRange!.start);
        const endDate = new Date(filters.dateRange!.end);
        return pairDate >= startDate && pairDate <= endDate;
      });
    }

    if (filters.focus === 'productive') {
      result = result.filter((pair) =>
        (pair.clutches || []).some((clutch) =>
          (clutch.eggs || []).some((egg) => egg.status === EggStatus.HATCHED),
        ),
      );
    } else if (filters.focus === 'attention') {
      const recentCutoff = daysAgo(90);
      result = result.filter((pair) => {
        const isActive = !pair.pairingEndDate;
        const hasRecentClutch = (pair.clutches || []).some(
          (clutch) => new Date(clutch.date) > recentCutoff,
        );
        const eggs = (pair.clutches || []).flatMap((clutch) => clutch.eggs || []);
        const failed = eggs.filter((egg) =>
          [
            EggStatus.NOT_FERTILIZED,
            EggStatus.BROKEN,
            EggStatus.ABANDONED,
            EggStatus.DEAD_IN_SHELL,
            EggStatus.DEAD_BEFORE_BANDING,
          ].includes(egg.status),
        ).length;
        const highFailure = eggs.length > 0 && failed / eggs.length > 0.5;
        return (isActive && !hasRecentClutch) || highFailure;
      });
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      // Handle nested properties
      if (sortBy === 'maleBirdEntity' && a.maleBirdEntity && b.maleBirdEntity) {
        aVal = a.maleBirdEntity.bandId;
        bVal = b.maleBirdEntity.bandId;
      }
      if (sortBy === 'femaleBirdEntity' && a.femaleBirdEntity && b.femaleBirdEntity) {
        aVal = a.femaleBirdEntity.bandId;
        bVal = b.femaleBirdEntity.bandId;
      }
      
      // Handle undefined values
      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return sortOrder === 'asc' ? 1 : -1;
      if (bVal === undefined) return sortOrder === 'asc' ? -1 : 1;
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [breedingPairs, searchTerm, filters, sortBy, sortOrder]);

  // Pagination logic
  const totalPages = Math.ceil(filteredBreedingPairs.length / itemsPerPage);
  const paginatedBreedingPairs = filteredBreedingPairs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique values for filter options
  const speciesMap = new Map();
  breedingPairs.forEach(pair => {
    if (pair.maleBirdEntity?.species) {
      speciesMap.set(pair.maleBirdEntity.species.id, pair.maleBirdEntity.species);
    }
    if (pair.femaleBirdEntity?.species) {
      speciesMap.set(pair.femaleBirdEntity.species.id, pair.femaleBirdEntity.species);
    }
  });

  // Event handlers
  const handleAddBreedingPair = async (data: CreateBreedingPairDto) => {
    try {
      await breedingApi.create(data);
      await loadData();
      setShowAddModal(false);
      toast.success('Breeding pair added successfully');
    } catch (error) {
      console.error('Error adding breeding pair:', error);
      toast.error('Failed to add breeding pair');
    }
  };

  const handleEditBreedingPair = async (id: string, data: Partial<BreedingPair>) => {
    try {
      await breedingApi.update(id, data);
      await loadData();
      
      // Update selectedBreedingPair if it's the one being edited
      if (selectedBreedingPair && selectedBreedingPair.id === id) {
        const refreshed = await breedingApi.getById(id);
        setSelectedBreedingPair(refreshed.data);
      }
      
      setShowEditModal(false);
      toast.success('Breeding pair updated successfully');
    } catch (error) {
      console.error('Error updating breeding pair:', error);
      toast.error('Failed to update breeding pair');
    }
  };

  const handleManageClutchesPairUpdated = (updatedPair: BreedingPair) => {
    setBreedingPairs((prev) =>
      prev.map((pair) => (pair.id === updatedPair.id ? updatedPair : pair)),
    );
    setSelectedBreedingPair(updatedPair);
  };

  const handleDeleteBreedingPair = async () => {
    if (!selectedBreedingPair) return;
    
    try {
      await breedingApi.delete(selectedBreedingPair.id);
      await loadData();
      setShowDeleteModal(false);
      setSelectedBreedingPair(null);
      toast.success('Breeding pair deleted successfully');
    } catch (error) {
      console.error('Error deleting breeding pair:', error);
      toast.error('Failed to delete breeding pair');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
    setSearchParams({});
  };

  const focusLabel =
    filters.focus === 'productive'
      ? 'Most productive pairs (with hatched eggs)'
      : filters.focus === 'attention'
        ? 'Pairs needing attention'
        : filters.status === 'active'
          ? 'Active breeding pairs'
          : filters.status === 'ended'
            ? 'Ended breeding pairs'
            : null;

  const openViewModal = (pair: BreedingPair) => {
    setSelectedBreedingPair(pair);
    setShowViewModal(true);
  };

  const openEditModal = (pair: BreedingPair) => {
    setSelectedBreedingPair(pair);
    setShowEditModal(true);
  };

  const openDeleteModal = (pair: BreedingPair) => {
    setSelectedBreedingPair(pair);
    setShowDeleteModal(true);
  };

  const openManageClutchesModal = (pair: BreedingPair) => {
    setSelectedBreedingPair(pair);
    setShowManageClutchesModal(true);
  };

  const getStatusBadge = (pair: BreedingPair) => {
    const isActive = !pair.pairingEndDate;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          isActive
            ? 'bg-emerald-100 text-emerald-800'
            : 'bg-violet-100 text-violet-800'
        }`}
      >
        {isActive ? 'Active' : 'Ended'}
      </span>
    );
  };

  const getPairSpecies = (pair: BreedingPair) => {
    const maleSpecies = pair.maleBirdEntity?.species?.commonName;
    const femaleSpecies = pair.femaleBirdEntity?.species?.commonName;
    
    if (maleSpecies === femaleSpecies) {
      return maleSpecies || 'Unknown Species';
    }
    return `${maleSpecies || 'Unknown'} × ${femaleSpecies || 'Unknown'}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-semibold text-slate-900">Error</h3>
          <p className="mt-1 text-sm text-slate-500">{error}</p>
          <div className="mt-6">
            <button
              type="button"
              className="inline-flex items-center btn btn-primary text-sm shadow-sm"
              onClick={loadData}
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <PageHero
        title="Breeding Pairs"
        subtitle="Manage breeding pairs, track clutches, and monitor progress"
        actions={
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Breeding Pair
          </button>
        }
      />

      {focusLabel && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-md border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm text-cyan-900">
          <span>
            Showing: <span className="font-semibold">{focusLabel}</span>
          </span>
          <button type="button" onClick={clearFilters} className="font-medium text-cyan-700 hover:text-cyan-900">
            Clear filter
          </button>
        </div>
      )}

      {/* Search and Filter Controls */}
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md ring-1 ring-inset ring-gray-300 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              name="search"
              id="search"
              className="flex-1 min-w-0 py-1.5 pr-3 border-0 bg-transparent text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 sm:text-sm sm:leading-6"
              placeholder="Search breeding pairs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Filter Toggle */}
          <button
            type="button"
            className={`relative inline-flex items-center px-3 py-2 text-sm font-semibold ${
              showFilters
                ? 'bg-cyan-600 text-white'
                : 'bg-white text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80'
            } rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FunnelIcon className="h-4 w-4 mr-2" />
            Filters
            {Object.keys(filters).length > 0 && (
              <span className="ml-1 inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-700">
                {Object.keys(filters).length}
              </span>
            )}
          </button>

          {/* View Mode Toggle */}
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-cyan-600 text-white border-cyan-500'
                  : 'bg-white text-slate-700 border-gray-300 hover:bg-slate-50/80'
              }`}
              onClick={() => setViewMode('grid')}
            >
              <Squares2X2Icon className="h-4 w-4" />
            </button>
            <button
              type="button"
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-b border-r ${
                viewMode === 'table'
                  ? 'bg-cyan-600 text-white border-cyan-500'
                  : 'bg-white text-slate-700 border-gray-300 hover:bg-slate-50/80'
              }`}
              onClick={() => setViewMode('table')}
            >
              <TableCellsIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <Transition
        show={showFilters}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <div className="mt-4 bg-slate-50/80 p-4 rounded-lg">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value as 'active' | 'ended' || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297360" value="">All Status</option>
                <option key="active-1753461297360" value="active">Active</option>
                <option key="ended-1753461297360" value="ended">Ended</option>
              </select>
            </div>

            {/* Cage Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Cage Number
              </label>
              <CageSelect
                id="filterCage"
                value={filters.cageNumber || ''}
                onChange={(cageNumber) => setFilters((prev) => ({ ...prev, cageNumber: cageNumber || undefined }))}
                allowEmpty
                allowCreate={false}
                includeInactive
                emptyLabel="All cages"
              />
            </div>

            {/* Species Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Species
              </label>
              <select
                value={filters.speciesId || ''}
                onChange={(e) => setFilters(prev => ({ ...prev, speciesId: e.target.value || undefined }))}
                className="block w-full input border-gray-300 shadow-sm sm:text-sm"
              >
                <option key="empty-option-1753461297360" value="">All Species</option>
                {Array.from(speciesMap.values()).map(species => (
                  <option key={species.id} value={species.id}>{species.commonName}</option>
                ))}
              </select>
            </div>

            {/* Clear Filters */}
            <div className="flex items-end">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full inline-flex justify-center items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Clear
              </button>
            </div>
          </div>
        </div>
      </Transition>

      {/* Results Summary */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-slate-700">
          Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
          <span className="font-medium">
            {Math.min(currentPage * itemsPerPage, filteredBreedingPairs.length)}
          </span> of{' '}
          <span className="font-medium">{filteredBreedingPairs.length}</span> breeding pairs
        </p>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-700">Sort by:</label>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as keyof BreedingPair)}
            className="rounded-md border-gray-300 text-sm focus:border-cyan-500 focus:ring-cyan-500"
          >
            <option key="pairingBeginDate-1753461297360" value="pairingBeginDate">Pairing Date</option>
            <option key="cageNumber-1753461297360" value="cageNumber">Cage Number</option>
            <option key="maleBirdEntity-1753461297360" value="maleBirdEntity">Male Bird</option>
            <option key="femaleBirdEntity-1753461297360" value="femaleBirdEntity">Female Bird</option>
          </select>
          <SortOrderButton
            order={sortOrder}
            onToggle={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Content */}
      {filteredBreedingPairs.length === 0 ? (
        <EmptyState
          title="No breeding pairs found"
          description={
            searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filters.'
              : 'Get started by creating your first breeding pair.'
          }
          icon={<UserGroupIcon className="h-6 w-6" />}
          action={
            !searchTerm && Object.keys(filters).length === 0 ? (
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Breeding Pair
              </button>
            ) : undefined
          }
        />
      ) : viewMode === 'grid' ? (
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {paginatedBreedingPairs.map((pair) => (
            <div
              key={pair.id}
              className="col-span-1 flex flex-col divide-y divide-gray-200 rounded-lg bg-white text-center shadow"
            >
              <div className="flex flex-1 flex-col p-8">
                <BreedingPairAvatars
                  maleBird={pair.maleBirdEntity}
                  femaleBird={pair.femaleBirdEntity}
                  size="xl"
                  showBandIds={true}
                  bandIdPosition="below"
                  layout="horizontal"
                  className="mb-6"
                />
                <h3 className="mt-6 text-sm font-medium text-slate-900">
                  {pair.maleBirdEntity ? getBirdDisplayId(pair.maleBirdEntity) : 'Unknown'} × {pair.femaleBirdEntity ? getBirdDisplayId(pair.femaleBirdEntity) : 'Unknown'}
                </h3>
                <dl className="mt-1 flex flex-grow flex-col justify-between">
                  <dt className="sr-only">Species</dt>
                  <dd className="text-sm text-slate-500">{getPairSpecies(pair)}</dd>
                  
                  <dt className="sr-only">Cage</dt>
                  <dd className="mt-3">
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <HomeIcon className="h-4 w-4" />
                      Cage {pair.cageNumber}
                    </div>
                  </dd>
                  
                  <dt className="sr-only">Date</dt>
                  <dd className="mt-1">
                    <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                      <CalendarIcon className="h-4 w-4" />
                      {pair.pairingBeginDate ? format(new Date(pair.pairingBeginDate), 'MMM d, yyyy') : 'No date set'}
                    </div>
                  </dd>
                  
                  <dt className="sr-only">Status</dt>
                  <dd className="mt-3">{getStatusBadge(pair)}</dd>
                  
                  {pair.clutches && pair.clutches.length > 0 && (
                    <>
                      <dt className="sr-only">Clutches</dt>
                      <dd className="mt-2">
                        <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
                          <ClipboardDocumentListIcon className="h-4 w-4" />
                          {pair.clutches.length} clutch{pair.clutches.length !== 1 ? 'es' : ''}
                        </div>
                      </dd>
                    </>
                  )}
                </dl>
              </div>
              <div>
                <div className="-mt-px flex divide-x divide-gray-200">
                  <div className="flex w-0 flex-1">
                    <button
                      onClick={() => openViewModal(pair)}
                      className="relative -mr-px inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-bl-lg border border-transparent py-4 text-sm font-semibold text-slate-900 hover:bg-slate-50/80"
                    >
                      <EyeIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                      View
                    </button>
                  </div>
                  <div className="-ml-px flex w-0 flex-1">
                    <button
                      onClick={() => openEditModal(pair)}
                      className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 border border-transparent py-4 text-sm font-semibold text-slate-900 hover:bg-slate-50/80"
                    >
                      <PencilIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                      Edit
                    </button>
                  </div>
                  <div className="-ml-px flex w-0 flex-1">
                    <button
                      onClick={() => openManageClutchesModal(pair)}
                      className="relative inline-flex w-0 flex-1 items-center justify-center gap-x-3 rounded-br-lg border border-transparent py-4 text-sm font-semibold text-slate-900 hover:bg-slate-50/80"
                    >
                      <ClipboardDocumentListIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
                      Clutches
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-900 sm:pl-6">
                        Breeding Pair
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Species
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Cage
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Pairing Date
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Status
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-slate-900">
                        Clutches
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedBreedingPairs.map((pair) => (
                        <tr key={pair.id} className="hover:bg-slate-50/80">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <BreedingPairAvatars
                                  maleBird={pair.maleBirdEntity}
                                  femaleBird={pair.femaleBirdEntity}
                                  size="sm"
                                  layout="horizontal"
                                />
                              </div>
                              <div className="ml-4">
                                <div className="font-medium text-slate-900">
                                  {pair.maleBirdEntity ? getBirdDisplayId(pair.maleBirdEntity) : 'Unknown'} × {pair.femaleBirdEntity ? getBirdDisplayId(pair.femaleBirdEntity) : 'Unknown'}
                                </div>
                                <div className="text-slate-500">
                                  <span className="text-cyan-600">{GENDER_ICON_MALE}</span> {pair.maleBirdEntity ? getBirdDisplayId(pair.maleBirdEntity) : 'Unknown'} •
                                  <span className="text-pink-600 ml-1">{GENDER_ICON_FEMALE}</span> {pair.femaleBirdEntity ? getBirdDisplayId(pair.femaleBirdEntity) : 'Unknown'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            {getPairSpecies(pair)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            <div className="flex items-center">
                              <HomeIcon className="h-4 w-4 mr-1" />
                              {pair.cageNumber}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 mr-1" />
                              {pair.pairingBeginDate ? format(new Date(pair.pairingBeginDate), 'MMM d, yyyy') : 'No date set'}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            {getStatusBadge(pair)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-slate-500">
                            <div className="flex items-center">
                              <ClipboardDocumentListIcon className="h-4 w-4 mr-1" />
                              {pair.clutches?.length || 0}
                            </div>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <div className="flex items-center justify-end space-x-2">
                              <button
                                onClick={() => openViewModal(pair)}
                                className="text-cyan-600 hover:text-indigo-900"
                                title="View Details"
                              >
                                <EyeIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openEditModal(pair)}
                                className="text-cyan-600 hover:text-indigo-900"
                                title="Edit"
                              >
                                <PencilIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openManageClutchesModal(pair)}
                                className="text-cyan-600 hover:text-indigo-900"
                                title="Manage Clutches"
                              >
                                <ClipboardDocumentListIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => openDeleteModal(pair)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing page <span className="font-medium">{currentPage}</span> of{' '}
                <span className="font-medium">{totalPages}</span>
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-l-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                        pageNum === currentPage
                          ? 'z-10 bg-cyan-600 text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-600'
                          : 'text-slate-900 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center rounded-r-md px-2 py-2 text-slate-400 ring-1 ring-inset ring-gray-300 hover:bg-slate-50/80 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span className="sr-only">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddBreedingPairModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddBreedingPair}
      />

      {selectedBreedingPair && (
        <>
          <EditBreedingPairModal
            isOpen={showEditModal}
            onClose={() => {
              setShowEditModal(false);
              setSelectedBreedingPair(null);
            }}
            onEdit={handleEditBreedingPair}
            breedingPair={selectedBreedingPair}
          />
          
          <ViewBreedingPairModal
            isOpen={showViewModal}
            onClose={() => {
              setShowViewModal(false);
              setSelectedBreedingPair(null);
            }}
            breedingPair={selectedBreedingPair}
          />
          
          <ManageClutchesModal
            isOpen={showManageClutchesModal}
            onClose={() => {
              setShowManageClutchesModal(false);
              setSelectedBreedingPair(null);
            }}
            breedingPair={selectedBreedingPair}
            onPairUpdated={handleManageClutchesPairUpdated}
          />
        </>
      )}

      {/* Delete Confirmation Modal */}
      <BrandModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)} maxWidthClass="max-w-lg">
        <BrandModalHeader title="Delete Breeding Pair" onClose={() => setShowDeleteModal(false)} />
        <BrandModalBody>
          <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
            <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <p className="text-sm text-slate-600 text-center">
            Are you sure you want to delete this breeding pair? This action cannot be undone and will also delete all associated clutches and eggs.
          </p>
        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            className="btn btn-secondary text-sm"
            onClick={() => setShowDeleteModal(false)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger text-sm"
            onClick={handleDeleteBreedingPair}
          >
            Delete
          </button>
        </BrandModalFooter>
      </BrandModal>
    </div>
  );
} 